"use client";
import React, { useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Image, Upload, message } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";

type RcCustomRequestOptions = {
  file: File | Blob | string;
  onSuccess?: (body: any, xhr?: XMLHttpRequest) => void;
  onError?: (event: Error) => void;
};

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const getBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export interface MediaUploadFn {
  (input: {
    filename: string;
    filetype: string;
    base64String: string;
  }): Promise<{ data: { url: string } }>;
}

interface UploaderProps {
  fileList: UploadFile[];
  setFileList: (fileList: UploadFile[]) => void;
  uploadMedia: MediaUploadFn;
}

const Uploader = ({ fileList, setFileList, uploadMedia }: UploaderProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList }) => {
    setFileList(fileList);
  };

  const uploadButton = (
    // color must be inherited: a native button defaults to the UA's
    // buttontext (black), which disappears on dark booking skins.
    <button
      style={{ border: 0, background: "none", color: "inherit" }}
      type="button"
    >
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const customRequest = async ({
    file,
    onSuccess,
    onError,
  }: RcCustomRequestOptions) => {
    try {
      const f = file as File;
      const dataUrl = await getBase64(f as Blob);
      const [, base64String] = (dataUrl as string).split(",");
      const res = await uploadMedia({
        filename: f.name,
        filetype: f.type,
        base64String,
      });
      onSuccess && onSuccess(res.data as any, new XMLHttpRequest());
    } catch (e: any) {
      message.error(e?.message || "Upload failed");
      onError && onError(e);
    }
  };

  return (
    <>
      <Upload
        customRequest={customRequest}
        listType="picture-card"
        fileList={fileList}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={() => {}}
      >
        {fileList.length >= 8 ? null : uploadButton}
      </Upload>
      {previewImage && (
        <Image
          alt="preview"
          wrapperStyle={{ display: "none" }}
          preview={{
            open: previewOpen,
            onOpenChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewImage(""),
          }}
          src={previewImage}
        />
      )}
    </>
  );
};

export default React.memo(Uploader);
